/**
 * Gráfico Stacked Bar Horizontal para Pendências por Tipo x Fornecedor
 * Eixo Y: PendencyTypeName
 * Barras stacked: Distribuídas por fornecedor
 * Label: Total no final da barra
 * Click: Abre modal com detalhes das unidades
 */
import { useMemo, useState, useEffect, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { cn } from '@/lib/utils';
import { PendencyDetailsModal } from './PendencyDetailsModal';

// Hook para detectar dark mode
function useIsDarkMode() {
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains('dark')
  );

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    return () => observer.disconnect();
  }, []);

  return isDark;
}

/** Contagem por fornecedor */
export interface SupplierCount {
  supplier: string;
  count: number;
}

/** Linha de pendência */
export interface PendencyRow {
  pendencyType: string;
  total: number;
  bySupplier: SupplierCount[];
}

/** Resposta do endpoint */
export interface PendenciesResponse {
  rows: PendencyRow[];
  suppliers: string[];
  grandTotal: number;
  availableFilters: {
    suppliers: string[];
    pendencyTypes: string[];
  };
}

interface PendenciasFornecedorChartProps {
  data: PendencyRow[];
  suppliers: string[];
  isLoading?: boolean;
  className?: string;
  filters?: {
    regional?: string;
    uf?: string;
    docTypes?: string;
    suppliers?: string;
  };
}

const BAR_COLOR = '#2563eb';

export function PendenciasFornecedorChart({
  data,
  suppliers,
  isLoading = false,
  className,
  filters = {},
}: PendenciasFornecedorChartProps) {
  const isDark = useIsDarkMode();
  const textColor = isDark ? '#e5e7eb' : '#374151';
  const chartRef = useRef<ReactECharts>(null);
  
  // Estado para modal de detalhes
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPendencyType, setSelectedPendencyType] = useState<string>('');

  // Handler para click no gráfico
  const handleChartClick = (params: { dataIndex?: number; name?: string }) => {
    if (params.dataIndex !== undefined && data[params.dataIndex]) {
      const pendencyType = data[params.dataIndex].pendencyType;
      setSelectedPendencyType(pendencyType);
      setModalOpen(true);
    }
  };

  const option: EChartsOption = useMemo(() => {
    if (!data || data.length === 0) return {};

    // Categorias = PendencyTypes
    const categories = data.map((r) => r.pendencyType);

    const series = [{
      name: 'Pendências',
      type: 'bar' as const,
      emphasis: { focus: 'series' as const },
      itemStyle: {
        color: BAR_COLOR,
        opacity: 0.9,
      },
      label: {
        show: true,
        position: 'right' as const,
        formatter: (params: { dataIndex: number }) => {
          const row = data[params.dataIndex];
          return row && typeof row.total === 'number' ? row.total.toLocaleString('pt-BR') : '';
        },
        color: textColor,
        fontWeight: 'bold' as const,
        fontSize: 11,
      },
      data: data.map((r) => r.total),
    }];

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: unknown) => {
          const paramsArray = params as Array<{
            dataIndex: number;
            name: string;
            marker: string;
            seriesName: string;
            value: number;
          }>;
          if (!paramsArray.length) return '';

          const row = data[paramsArray[0].dataIndex];
          return `<strong>${row.pendencyType}</strong><br/>
            <strong>Total: ${row.total.toLocaleString('pt-BR')}</strong>`;
        },
      },
      grid: {
        left: 220,
        right: 60,
        top: 40,
        bottom: 20,
        containLabel: false,
      },
      xAxis: {
        type: 'value',
        axisLabel: {
          color: textColor,
          formatter: (v: number) => v.toLocaleString('pt-BR'),
        },
        splitLine: {
          lineStyle: { color: isDark ? '#374151' : '#e5e7eb' },
        },
      },
      yAxis: {
        type: 'category',
        data: categories,
        inverse: true,
        axisLabel: {
          color: textColor,
          fontSize: 11,
          width: 200,
          overflow: 'truncate',
          formatter: (value: string) => {
            return value.length > 30 ? value.slice(0, 27) + '...' : value;
          },
        },
        axisTick: { show: false },
        axisLine: {
          lineStyle: { color: isDark ? '#374151' : '#e5e7eb' },
        },
      },
      series,
    };
  }, [data, suppliers, isDark, textColor]);

  // Altura baseada na quantidade de itens
  const chartHeight = Math.max(300, (data?.length || 0) * 40 + 80);

  if (isLoading) {
    return (
      <div className={cn('animate-pulse bg-muted rounded', className)} style={{ height: 300 }} />
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className={cn('flex items-center justify-center h-[200px] text-muted-foreground', className)}>
        Nenhuma pendência encontrada
      </div>
    );
  }

  return (
    <>
      <ReactECharts
        option={option}
        style={{ height: chartHeight, width: '100%', cursor: 'pointer' }}
        opts={{ renderer: 'svg' }}
        className={className}
        onEvents={{ click: handleChartClick }}
      />
      <PendencyDetailsModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        pendencyType={selectedPendencyType}
        filters={filters}
      />
    </>
  );
}
